import React, { useState, useRef, useEffect, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import { C } from '../styles/tokens'
import { useProducts } from '../context/ProductsContext'
import { API_BASE } from '../config'
import AuthContext from '../AuthContext'

function usePatientEmail() {
  const auth = useContext(AuthContext)
  return auth?.user?.email || null
}

// ── Quiz data ──────────────────────────────────────────────────────────────────
const STEPS = [
  {
    id: 'concern',
    question: "What's your main skin or hair concern?",
    subtitle: 'Choose the one that bothers you most.',
    options: [
      { label: 'Acne & Breakouts', emoji: '😣', value: 'acne' },
      { label: 'Dark Spots & Pigmentation', emoji: '🌑', value: 'pigmentation' },
      { label: 'Fine Lines & Ageing', emoji: '⏳', value: 'aging' },
      { label: 'Dry & Dull Skin', emoji: '💧', value: 'dry' },
      { label: 'Oily & Large Pores', emoji: '✨', value: 'oily' },
      { label: 'Redness & Sensitivity', emoji: '🌸', value: 'sensitive' },
      { label: 'Dark Circles / Under-Eye', emoji: '👁️', value: 'darkcircles' },
      { label: 'Uneven Texture', emoji: '🧩', value: 'texture' },
      { label: 'Hair Fall / Thinning', emoji: '💇', value: 'hair' },
      { label: 'Fungal / Itchy Skin', emoji: '🍄', value: 'fungal' },
    ],
  },
  {
    id: 'type',
    question: 'How would you describe your skin?',
    subtitle: 'This helps us pick the right texture and formula.',
    options: [
      { label: 'Oily / Greasy', emoji: '✨', value: 'oily' },
      { label: 'Dry / Flaky', emoji: '❄️', value: 'dry' },
      { label: 'Combination (T-zone oily)', emoji: '☯️', value: 'combo' },
      { label: 'Sensitive / Reactive', emoji: '🌸', value: 'sensitive' },
      { label: 'Normal / Balanced', emoji: '🙂', value: 'normal' },
    ],
  },
  {
    id: 'age',
    question: 'What is your age group?',
    subtitle: 'Skin needs change at every stage of life.',
    options: [
      { label: 'Under 20', emoji: '🧒', value: 'teen' },
      { label: '20 – 30', emoji: '🧑', value: '20s' },
      { label: '31 – 45', emoji: '👩', value: '30s' },
      { label: '46 +', emoji: '🧓', value: '45plus' },
    ],
  },
  {
    id: 'sun',
    question: 'Do you use sunscreen daily?',
    subtitle: 'Sun exposure drives most pigmentation and ageing.',
    options: [
      { label: 'Yes, every day', emoji: '🧴', value: 'daily' },
      { label: 'Sometimes', emoji: '🌤️', value: 'sometimes' },
      { label: 'Rarely', emoji: '🌥️', value: 'rarely' },
      { label: 'Never', emoji: '☀️', value: 'never' },
    ],
  },
  {
    id: 'sensitivity',
    question: 'How sensitive is your skin?',
    subtitle: 'Does it sting, redden, or react to new products?',
    options: [
      { label: 'Very sensitive — reacts often', emoji: '🥺', value: 'high' },
      { label: 'Occasionally reacts', emoji: '😐', value: 'medium' },
      { label: 'Rarely reacts', emoji: '😌', value: 'low' },
      { label: 'Never — tolerates anything', emoji: '💪', value: 'none' },
    ],
  },
  {
    id: 'routine',
    question: "What's your current routine like?",
    subtitle: 'So we match the right number of steps.',
    options: [
      { label: 'Nothing regular', emoji: '🌱', value: 'none' },
      { label: 'Basic (cleanse + moisturise)', emoji: '🧼', value: 'basic' },
      { label: 'Full multi-step routine', emoji: '🧪', value: 'extensive' },
    ],
  },
  {
    id: 'goal',
    question: "What's your main goal?",
    subtitle: 'What would make you happiest with your skin?',
    options: [
      { label: 'Clear, blemish-free skin', emoji: '✨', value: 'clear' },
      { label: 'Brighter, even tone', emoji: '🌟', value: 'brighten' },
      { label: 'Younger, firmer skin', emoji: '⏳', value: 'antiage' },
      { label: 'Deep hydration & glow', emoji: '💧', value: 'hydrate' },
      { label: 'Stronger, fuller hair', emoji: '💇', value: 'hairgrowth' },
    ],
  },
  {
    id: 'safety',
    question: 'Do any of these apply to you?',
    subtitle: 'So we avoid ingredients that may not be right for you.',
    options: [
      { label: 'Pregnant or breastfeeding', emoji: '🤰', value: 'pregnant' },
      { label: 'Very reactive to actives', emoji: '⚠️', value: 'reactive' },
      { label: 'None of these', emoji: '✅', value: 'none' },
    ],
  },
  {
    id: 'existing',
    question: 'Are you already using any prescription products?',
    subtitle: 'We will suggest complementary options.',
    options: [
      { label: 'No, starting fresh', emoji: '🌱', value: 'none' },
      { label: 'Yes, using some', emoji: '💊', value: 'using' },
      { label: 'Tried many, nothing worked', emoji: '😞', value: 'failed' },
    ],
  },
]

const CONCERN_TAGS = {
  acne:         ['Acne', 'Anti-Acne', 'Acne Care', 'Acne Prone', 'acne marks'],
  hair:         ['Hair Fall / Hair Growth', 'Hair Growth', 'Hair Care', 'hair follicle'],
  pigmentation: ['Dark Spots', 'depigmenting', 'Depigmenting Cream', 'Dark Spot Reduction', 'Brighten Skin'],
  dry:          ['Dry Skin', 'Hydration / Moisturization', 'Hydrate Skin', 'barrier repair'],
  oily:         ['Oil Control', 'Oily Skin', 'Sebum', 'Mattifying', 'Pores', 'Salicylic'],
  aging:        ['Anti-Aging', 'Anti Aging', 'Wrinkles', 'Fine Lines', 'retinol', 'Collagen', 'Firming'],
  sensitive:    ['Sensitive', 'Soothing', 'Redness', 'barrier repair', 'Calming', 'Ceramide'],
  darkcircles:  ['Dark Circles', 'Under Eye', 'Under-Eye', 'Eye Care', 'under eye'],
  texture:      ['Exfoliation', 'Texture', 'Pores', 'Uneven', 'AHA', 'BHA', 'Glycolic', 'Lactic'],
  fungal:       ['Anti Fungal', 'Antifungal', 'Fungal'],
  eczema:       ['Eczema', 'Eczema Cream', 'irritation', 'Dermatitis'],
}
const CONCERN_CATS = {
  acne:         ['Skincare', 'Serums', 'Face Wash'],
  hair:         ['Hair Care', 'Serums'],
  pigmentation: ['De-Pigmenting', 'Serums'],
  dry:          ['Moisturizers'],
  oily:         ['Face Wash', 'Serums', 'Skincare'],
  aging:        ['Serums', 'Moisturizers'],
  sensitive:    ['Moisturizers', 'Skincare'],
  darkcircles:  ['Serums', 'Skincare'],
  texture:      ['Serums', 'Face Wash'],
  fungal:       ['Skincare'],
  eczema:       ['Skincare', 'Moisturizers'],
}

function getRecommendations(concern, ALL_PRODUCTS) {
  const tagList = CONCERN_TAGS[concern] || []
  const catList = CONCERN_CATS[concern] || []
  return ALL_PRODUCTS
    .map(p => {
      let score = 0
      const ptags = (p.tags || []).map(t => t.toLowerCase())
      tagList.forEach(t => { if (ptags.some(pt => pt.includes(t.toLowerCase()))) score += 2 })
      if (catList.includes(p.cat)) score += 1
      return { ...p, _score: score }
    })
    .filter(p => p._score > 0)
    .sort((a, b) => b._score - a._score)
    .slice(0, 4)
}

// ── Severity colour ────────────────────────────────────────────────────────────
const SEV_COLOR = { mild: '#3D8A57', moderate: '#D97706', severe: '#DC2626' }
const SEV_BG    = { mild: '#F0FDF4', moderate: '#FFFBEB', severe: '#FEF2F2' }

// Unique per-condition palette so each circle/card has its own colour
const COND_PALETTE = [
  '#7C3AED', // violet
  '#2563EB', // blue
  '#0891B2', // cyan
  '#D97706', // amber
  '#DC2626', // red
  '#DB2777', // pink
  '#059669', // emerald
  '#EA580C', // orange
]
function condColor(idx) { return COND_PALETTE[idx % COND_PALETTE.length] }

// Fallback face region positions (used when MediaPipe/Gemini coords not available)
const FACE_REGIONS = {
  forehead:    [{ x: 50, y: 14 }],
  left_cheek:  [{ x: 24, y: 50 }],
  right_cheek: [{ x: 76, y: 50 }],
  cheeks:      [{ x: 24, y: 50 }, { x: 76, y: 50 }],
  nose:        [{ x: 50, y: 40 }],
  chin:        [{ x: 50, y: 66 }],
  under_eyes:  [{ x: 35, y: 30 }, { x: 65, y: 30 }],
  neck:        [{ x: 50, y: 85 }],
  overall:     [{ x: 50, y: 44 }],
}

// ── MediaPipe Face Mesh integration ───────────────────────────────────────────
// Landmark indices → face regions (468-point model, anatomical naming)
// In a raw selfie: anatomical LEFT cheek is on IMAGE RIGHT (x>0.5)
//                  anatomical RIGHT cheek is on IMAGE LEFT (x<0.5)
const LM_REGIONS = {
  forehead:    { pts: [10, 9, 151] },
  left_cheek:  { pts: [425, 411, 376] },
  right_cheek: { pts: [205, 187, 147] },
  cheeks:      { pts: [[425, 411], [205, 187]], multi: true },
  nose:        { pts: [1, 4, 94] },
  chin:        { pts: [152, 175] },
  under_eyes:  { pts: [[374, 380], [145, 153]], multi: true },
  neck:        { pts: [152] },
  overall:     { pts: [168] },
}

function lmPct(landmarks, indices) {
  const pts = indices.map(i => landmarks[i]).filter(Boolean)
  if (!pts.length) return null
  return {
    x: pts.reduce((s, p) => s + p.x, 0) / pts.length * 100,
    y: pts.reduce((s, p) => s + p.y, 0) / pts.length * 100,
  }
}

function buildRegionMap(landmarks) {
  const map = {}
  for (const [region, def] of Object.entries(LM_REGIONS)) {
    if (def.multi) {
      map[region] = def.pts.map(group => lmPct(landmarks, group)).filter(Boolean)
    } else {
      const pt = lmPct(landmarks, def.pts)
      map[region] = pt ? [pt] : []
    }
  }
  return map
}

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
    const s = document.createElement('script')
    s.src = src; s.onload = resolve; s.onerror = reject
    document.head.appendChild(s)
  })
}

async function initFaceMesh() {
  await loadScript('https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/face_mesh.js')
  const fm = new window.FaceMesh({
    locateFile: file => `https://cdn.jsdelivr.net/npm/@mediapipe/face_mesh/${file}`
  })
  fm.setOptions({ maxNumFaces: 1, refineLandmarks: true, minDetectionConfidence: 0.5, minTrackingConfidence: 0.5 })
  await fm.initialize()
  return fm
}

async function detectFaceLandmarks(faceMesh, dataUrl) {
  if (!faceMesh) return null
  return new Promise(resolve => {
    let done = false
    faceMesh.onResults(results => {
      if (done) return; done = true
      const lm = results.multiFaceLandmarks?.[0]
      resolve(lm ? buildRegionMap(lm) : null)
    })
    const img = new Image()
    img.onload  = () => faceMesh.send({ image: img }).catch(() => { if (!done) { done = true; resolve(null) } })
    img.onerror = () => resolve(null)
    img.src = dataUrl
  })
}
// Which condition areas are visible from each angle
// Front shows all; left side hides right_cheek-only conditions; right side hides left_cheek-only
// Include both cheeks on side photos — Gemini may label the visible cheek
// as either left or right depending on image orientation
const ANGLE_VISIBLE_AREAS = [
  null,
  ['left_cheek', 'cheeks', 'overall', 'forehead', 'nose', 'chin', 'under_eyes', 'neck'],
  ['right_cheek', 'cheeks', 'overall', 'forehead', 'nose', 'chin', 'under_eyes', 'neck'],
]

function AnnotatedPhoto({ src, conditions, onTap, visibleAreas = null, landmarkMap = null, photoIdx = 0 }) {
  // Priority: 1) per-photo Python coords  2) MediaPipe browser landmarks  3) Gemini x,y  4) fixed fallback
  const raw = []
  ;(conditions || []).forEach((cond, condIdx) => {
    const key = (cond.area || '').toLowerCase().replace(/[\s-]+/g, '_')
    if (visibleAreas && !visibleAreas.includes(key)) return
    if (key === 'overall') return   // overall conditions have no single pinpoint location

    // Placement priority — region-first, because the AI is reliable at naming the
    // face region but poor at guessing exact pixels. Only use its raw x,y if the
    // region is unknown.
    //   1) per-photo detector coords  2) MediaPipe landmark for the region
    //   3) fixed region map            4) AI x,y (last resort)
    const perPhoto = cond.photo_coords?.[photoIdx]
    const regionPts = FACE_REGIONS[key]
    if (perPhoto?.x != null) {
      raw.push({ x: Number(perPhoto.x), y: Number(perPhoto.y), color: condColor(condIdx), num: condIdx + 1 })
    } else if (landmarkMap?.[key]?.length) {
      landmarkMap[key].forEach(pt => raw.push({ x: pt.x, y: pt.y, color: condColor(condIdx), num: condIdx + 1 }))
    } else if (regionPts?.length) {
      regionPts.forEach(pt => raw.push({ x: pt.x, y: pt.y, color: condColor(condIdx), num: condIdx + 1 }))
    } else if (cond.x != null && cond.y != null) {
      raw.push({ x: Number(cond.x), y: Number(cond.y), color: condColor(condIdx), num: condIdx + 1 })
    }
  })

  // Clamp to face-visible area
  const clamp = (m) => ({ ...m, x: Math.min(Math.max(m.x, 12), 88), y: Math.min(Math.max(m.y, 8), 88) })

  // Spread circles radially so they never overlap
  const SPREAD_ANGLES = [0, 120, 240, 60, 180, 300]
  const SPREAD_DIST = 13
  const markers = raw.map((m, i) => {
    const clamped = clamp(m)
    const nearby = raw.filter((o, j) => j < i && Math.abs(o.x - clamped.x) < 10 && Math.abs(o.y - clamped.y) < 10)
    if (nearby.length === 0) return clamped
    const angle = SPREAD_ANGLES[nearby.length % SPREAD_ANGLES.length] * Math.PI / 180
    return clamp({ ...clamped, x: clamped.x + Math.cos(angle) * SPREAD_DIST, y: clamped.y + Math.sin(angle) * SPREAD_DIST })
  })

  return (
    <div onClick={onTap} style={{ position: 'relative', transform: 'scaleX(-1)', borderRadius: 16, overflow: 'hidden', cursor: 'zoom-in' }}>
      <img src={src} alt="" style={{ width: '100%', display: 'block' }} />
      {markers.map((m, i) => (
        <div key={i} style={{ position: 'absolute', left: `${m.x}%`, top: `${m.y}%`, transform: 'translate(-50%,-50%)', width: '12%', aspectRatio: '1', borderRadius: '50%', border: `2.5px solid ${m.color}`, background: `${m.color}28`, pointerEvents: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ color: 'white', fontSize: '.72rem', fontWeight: 800, textShadow: `0 0 4px ${m.color}`, transform: 'scaleX(-1)', lineHeight: 1 }}>{m.num}</span>
          <div style={{ position: 'absolute', inset: -5, borderRadius: '50%', border: `2px solid ${m.color}70`, animation: 'pulse-ring 2s ease-out infinite' }} />
        </div>
      ))}
      <div style={{ position: 'absolute', bottom: 8, right: 8, background: 'rgba(0,0,0,.55)', color: 'white', fontSize: '.6rem', padding: '3px 8px', borderRadius: 999, transform: 'scaleX(-1)' }}>
        🔍 Tap to zoom
      </div>
    </div>
  )
}

// ── AI Skin Scan component ─────────────────────────────────────────────────────
function SkinScan({ onBack }) {
  const navigate      = useNavigate()
  const patientEmail  = usePatientEmail()
  const videoRef      = useRef(null)
  const streamRef     = useRef(null)
  const faceMeshRef   = useRef(null)
  const retryTickRef  = useRef(null)
  const retryCountRef = useRef(0)
  const [phase, setPhase]         = useState('intro')
  const [photos, setPhotos]       = useState([])
  const [landmarkMaps, setLandmarkMaps] = useState([])
  const [analysis, setAnalysis]   = useState(null)
  const [errMsg, setErrMsg]       = useState('')
  const [dots, setDots]           = useState('')
  const [saved, setSaved]         = useState(false)
  const [expanded, setExpanded]   = useState(false)
  const [activePhotoIdx, setActivePhotoIdx] = useState(0)
  const [countdown, setCountdown] = useState(0)
  const [retryNote, setRetryNote] = useState('')
  const [pendingAnalyze, setPendingAnalyze] = useState(false)

  useEffect(() => {
    if (phase !== 'loading') return
    const t = setInterval(() => setDots(d => d.length >= 3 ? '' : d + '.'), 500)
    return () => clearInterval(t)
  }, [phase])

  useEffect(() => () => stopCamera(), [])

  // Restore pending photos after returning from login
  useEffect(() => {
    if (!patientEmail) return
    try {
      const raw = sessionStorage.getItem('skin_quiz_pending_photos')
      if (!raw) return
      const pending = JSON.parse(raw)
      sessionStorage.removeItem('skin_quiz_pending_photos')
      if (pending?.photos?.length) {
        setPhotos(pending.photos)
        setLandmarkMaps(pending.landmarkMaps || [])
        setPendingAnalyze(true)
      }
    } catch {}
  }, [patientEmail])

  // Trigger analyze once photos are in state (after restore)
  useEffect(() => {
    if (!pendingAnalyze || !photos.length || !patientEmail) return
    setPendingAnalyze(false)
    analyze()
  }, [pendingAnalyze, photos.length, patientEmail]) // eslint-disable-line

  // Load MediaPipe Face Mesh in the background on mount
  useEffect(() => {
    let cancelled = false
    initFaceMesh()
      .then(fm => { if (!cancelled) faceMeshRef.current = fm; console.log('[FaceMesh] ready') })
      .catch(e  => console.warn('[FaceMesh] load failed:', e.message))
    return () => { cancelled = true }
  }, [])

  async function startCamera() {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 720, height: 720 } })
      streamRef.current = stream
      setPhase('camera')
      setTimeout(() => { if (videoRef.current) videoRef.current.srcObject = stream }, 100)
    } catch(e) {
      setErrMsg('Camera access denied. Please allow camera permission and try again.')
      setPhase('error')
    }
  }

  function stopCamera() {
    streamRef.current?.getTracks().forEach(t => t.stop())
    streamRef.current = null
  }

  function capturePhoto() {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement('canvas')
    const W = Math.min(video.videoWidth || 720, 720)
    const H = Math.min(video.videoHeight || 720, 720)
    canvas.width  = W
    canvas.height = H
    canvas.getContext('2d').drawImage(video, 0, 0, W, H)
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
    const photoIdx = photos.length
    stopCamera()
    setPhotos(prev => [...prev, dataUrl])
    setPhase('preview')
    // Detect face landmarks in background — ready before user hits Analyze
    detectFaceLandmarks(faceMeshRef.current, dataUrl).then(map => {
      if (map) setLandmarkMaps(prev => { const n = [...prev]; n[photoIdx] = map; return n })
    })
  }

  async function analyze() {
    if (!patientEmail) { setPhase('login-required'); return }
    setPhase('loading')
    setErrMsg('')
    try {
      const res = await fetch(`${API_BASE}/api/skin-scan`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ images: photos, patientEmail })
      })
      let data
      try { data = await res.json() } catch { throw new Error('Server unavailable. Please restart the server and try again.') }

      // Schedule an auto-retry with a live countdown, then re-run analyze()
      const scheduleRetry = (secs, note) => {
        setRetryNote(note)
        setCountdown(secs)
        setPhase('retrying')
        retryTickRef.current = setInterval(() => {
          secs -= 1
          setCountdown(secs)
          if (secs <= 0) { clearInterval(retryTickRef.current); retryTickRef.current = null; analyze() }
        }, 1000)
      }

      if (res.status === 429) {
        // Model quota hit (or billing depleted). A brief blip refreshes in ~60s, but a
        // depleted/exhausted key never recovers — so cap retries instead of trapping the
        // user forever, then surface a clear message with a way out.
        if (retryCountRef.current < 3) {
          retryCountRef.current += 1
          scheduleRetry(75, 'The AI is busy right now. We’ll retry automatically.')
          return
        }
        throw new Error('Our AI is temporarily unavailable. Please try again later.')
      }
      if (res.status === 503 && retryCountRef.current < 4) {
        // High demand: our queue is full or we timed out waiting. It clears fast —
        // retry a few times on a short countdown before giving up.
        retryCountRef.current += 1
        scheduleRetry(18, 'High demand right now — you’re in the queue. We’ll retry automatically.')
        return
      }
      // 409 = this user already has a scan running; anything else = genuine error.
      if (!res.ok || !data.success) throw new Error(data.error || 'Analysis failed')
      retryCountRef.current = 0
      setAnalysis(data.analysis)
      setSaved(!!data.savedPath)
      setPhase('result')
    } catch(e) {
      setErrMsg(e.message || 'Analysis failed. Please try again.')
      setPhase('error')
    }
  }

  function retake() {
    if (retryTickRef.current) { clearInterval(retryTickRef.current); retryTickRef.current = null }
    retryCountRef.current = 0
    setCountdown(0)
    setRetryNote('')
    setPhotos([])
    setLandmarkMaps([])
    setAnalysis(null)
    setSaved(false)
    setExpanded(false)
    setActivePhotoIdx(0)
    setPendingAnalyze(false)
    sessionStorage.removeItem('skin_quiz_pending_photos')
    setPhase('intro')
  }

  const ANGLE_LABELS = ['Front', 'Left side', 'Right side']

  // ── INTRO ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
      <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🔬</div>
      <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.9rem', color: C.g900, marginBottom: '.6rem', lineHeight: 1.2 }}>AI Skin Analysis</h2>
      <p style={{ color: C.muted, fontSize: '.9rem', maxWidth: 360, margin: '0 auto 2rem', lineHeight: 1.6 }}>
        Take up to 3 photos from different angles — AI marks exactly where each condition is on your face.
      </p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.75rem', marginBottom: '1.5rem', textAlign: 'left', background: C.g50, borderRadius: 14, padding: '1.25rem' }}>
        {[['📸','Front face + side angles for best accuracy'],['🔬','AI circles exactly where dark spots, acne, wrinkles are'],['👨‍⚕️','Doctor reviews before your consultation']].map(([icon, text]) => (
          <div key={text} style={{ display: 'flex', alignItems: 'center', gap: '.75rem', fontSize: '.85rem', color: C.g700 }}>
            <span style={{ fontSize: '1.3rem' }}>{icon}</span><span>{text}</span>
          </div>
        ))}
      </div>

      {/* Privacy / consent notice */}
      <div style={{ background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '1.5rem', textAlign: 'left' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '.6rem' }}>
          <span style={{ fontSize: '1.1rem', marginTop: 2 }}>🔒</span>
          <div style={{ fontSize: '.78rem', color: '#0369A1', lineHeight: 1.6 }}>
            <strong style={{ display: 'block', marginBottom: '.2rem', fontSize: '.82rem' }}>Privacy notice</strong>
            Your photos are stored securely for <strong>3 days</strong> for analysis only.
            They are permanently kept only if you book an appointment.
            Photos are never shared without your consent.
          </div>
        </div>
      </div>

      <button onClick={startCamera} style={{ width: '100%', padding: '1rem', borderRadius: 12, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", marginBottom: '.75rem' }}>
        📷 I understand — Open Camera
      </button>
      <button onClick={onBack} style={{ width: '100%', padding: '.75rem', borderRadius: 12, background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
        Answer Quiz Instead
      </button>
    </div>
  )

  // ── CAMERA ────────────────────────────────────────────────────────────────
  if (phase === 'camera') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '1.5rem' }}>
      <p style={{ textAlign: 'center', color: C.muted, fontSize: '.82rem', marginBottom: '.75rem' }}>
        Photo {photos.length + 1} of 3 — <strong>{ANGLE_LABELS[photos.length]}</strong>
      </p>
      <div style={{ position: 'relative', borderRadius: 20, overflow: 'hidden', background: '#000', aspectRatio: '1/1' }}>
        <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
          <div style={{ width: '65%', aspectRatio: '3/4', border: '2px solid rgba(255,255,255,.5)', borderRadius: '50%' }} />
        </div>
        <div style={{ position: 'absolute', bottom: 16, left: 0, right: 0, textAlign: 'center', color: 'rgba(255,255,255,.8)', fontSize: '.75rem' }}>
          Position your face in the oval
        </div>
      </div>
      <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
        <button onClick={() => { stopCamera(); setPhase(photos.length > 0 ? 'preview' : 'intro') }}
          style={{ flex: 1, padding: '.8rem', borderRadius: 10, background: C.g50, color: C.g700, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
          Cancel
        </button>
        <button onClick={capturePhoto} style={{ flex: 2, padding: '.8rem', borderRadius: 10, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '.95rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
          📸 Capture
        </button>
      </div>
    </div>
  )

  // ── PREVIEW ───────────────────────────────────────────────────────────────
  if (phase === 'preview') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '1.5rem' }}>
      {/* Thumbnail strip */}
      <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1rem' }}>
        {photos.map((p, i) => (
          <div key={i} style={{ flex: 1, position: 'relative' }}>
            <img src={p} alt="" style={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 12, transform: 'scaleX(-1)', border: `2px solid ${C.g400}` }} />
            <div style={{ position: 'absolute', top: 4, left: 4, background: C.g700, color: 'white', fontSize: '.6rem', padding: '2px 6px', borderRadius: 999 }}>{ANGLE_LABELS[i]}</div>
            <button onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
              style={{ position: 'absolute', top: 4, right: 4, background: 'rgba(0,0,0,.6)', color: 'white', border: 'none', borderRadius: '50%', width: 20, height: 20, cursor: 'pointer', fontSize: '.7rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ✕
            </button>
          </div>
        ))}
        {photos.length < 3 && (
          <button onClick={startCamera}
            style={{ flex: 1, aspectRatio: '1', borderRadius: 12, border: `2px dashed ${C.border}`, background: C.g50, cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: '.7rem', gap: '.25rem', fontFamily: "'DM Sans',sans-serif" }}>
            <span style={{ fontSize: '1.5rem' }}>📷</span>
            <span>Add angle</span>
            <span style={{ fontSize: '.6rem' }}>{ANGLE_LABELS[photos.length]}</span>
          </button>
        )}
      </div>
      <div style={{ display: 'flex', gap: '1rem' }}>
        <button onClick={() => { setPhotos([]); setPhase('intro') }}
          style={{ flex: 1, padding: '.8rem', borderRadius: 10, background: C.g50, color: C.g700, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
          Start over
        </button>
        <button onClick={analyze} style={{ flex: 2, padding: '.8rem', borderRadius: 10, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '.95rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
          Analyze {photos.length} photo{photos.length > 1 ? 's' : ''} ✨
        </button>
      </div>
    </div>
  )

  // ── LOGIN REQUIRED ────────────────────────────────────────────────────────
  if (phase === 'login-required') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1.25rem' }}>🔒</div>
      <h3 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.4rem', color: C.g900, marginBottom: '.5rem' }}>
        Login to see your results
      </h3>
      <p style={{ color: C.muted, fontSize: '.88rem', marginBottom: '2rem', lineHeight: 1.6 }}>
        Your photos are ready. Login or create a free account to view your AI skin analysis and save results.
      </p>
      <button onClick={() => {
          try { sessionStorage.setItem('skin_quiz_pending_photos', JSON.stringify({ photos, landmarkMaps })) } catch {}
          navigate('/login?redirect=/skin-quiz')
        }}
        style={{ width: '100%', padding: '.9rem', borderRadius: 12, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '1rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif", marginBottom: '.75rem' }}>
        Login / Sign Up
      </button>
      <button onClick={retake}
        style={{ width: '100%', padding: '.75rem', borderRadius: 12, background: 'transparent', color: C.muted, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
        Cancel
      </button>
    </div>
  )

  // ── LOADING ───────────────────────────────────────────────────────────────
  if (phase === 'loading') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
      <div style={{ position: 'relative', width: 120, height: 120, margin: '0 auto 2rem' }}>
        <img src={photos[0]} alt="" style={{ width: 120, height: 120, borderRadius: '50%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
        <div style={{ position: 'absolute', inset: -6, borderRadius: '50%', border: `3px solid ${C.g200}`, borderTopColor: C.g600, animation: 'spin 1s linear infinite' }} />
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} @keyframes pulse-ring{0%,100%{opacity:.8;transform:translate(-50%,-50%) scale(1)}50%{opacity:.3;transform:translate(-50%,-50%) scale(1.15)}}`}</style>
      <h3 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.4rem', color: C.g900, marginBottom: '.5rem' }}>
        Analyzing {photos.length} photo{photos.length > 1 ? 's' : ''}{dots}
      </h3>
      <p style={{ color: C.muted, fontSize: '.85rem' }}>AI is mapping conditions to exact locations on your face</p>
      <p style={{ color: C.muted, fontSize: '.75rem', marginTop: '.25rem' }}>May take up to 1 minute if AI is busy</p>
    </div>
  )

  // ── RETRYING ─────────────────────────────────────────────────────────────
  if (phase === 'retrying') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: 80, height: 80, borderRadius: '50%', border: `6px solid ${C.g200}`, borderTopColor: C.g600, animation: 'spin 1s linear infinite', margin: '0 auto 1.5rem' }} />
      <h3 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.4rem', color: C.g900, marginBottom: '.5rem' }}>
        AI is busy
      </h3>
      <p style={{ color: C.muted, fontSize: '.9rem', marginBottom: '1.5rem' }}>
        Auto-retrying in <strong style={{ color: C.g700, fontSize: '1.2rem' }}>{countdown}s</strong>
      </p>
      <p style={{ color: C.muted, fontSize: '.78rem', marginBottom: '2rem' }}>
        {retryNote || 'The AI model is temporarily rate-limited. We\'ll retry automatically.'}
      </p>
      <button onClick={retake} style={{ padding: '.7rem 1.5rem', borderRadius: 10, background: C.g50, color: C.g700, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
        Cancel
      </button>
    </div>
  )

  // ── ERROR ─────────────────────────────────────────────────────────────────
  if (phase === 'error') return (
    <div style={{ maxWidth: 520, margin: '0 auto', padding: '3rem 1.5rem', textAlign: 'center' }}>
      <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>⚠️</div>
      <h3 style={{ color: C.g900, marginBottom: '.5rem' }}>Something went wrong</h3>
      <p style={{ color: C.muted, fontSize: '.85rem', marginBottom: '2rem' }}>{errMsg}</p>
      <button onClick={retake} style={{ padding: '.75rem 2rem', borderRadius: 10, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '.9rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
        Try Again
      </button>
    </div>
  )

  // ── RESULT ────────────────────────────────────────────────────────────────
  if (phase === 'result' && analysis) {
    const urgencyColor = { routine: C.g600, soon: '#D97706', urgent: '#DC2626' }
    const urgencyLabel = { routine: 'Routine check-up recommended', soon: 'See a doctor soon', urgent: 'Consult a doctor urgently' }

    // Conditions visible on the currently shown photo
    const visAreas = ANGLE_VISIBLE_AREAS[activePhotoIdx]
    const photoConds = visAreas
      ? analysis.conditions.filter(c => visAreas.includes((c.area || '').toLowerCase().replace(/[\s-]+/g, '_')))
      : analysis.conditions

    return (
      <div style={{ maxWidth: 520, margin: '0 auto', padding: '1rem' }}>
        <style>{`@keyframes pulse-ring{0%,100%{opacity:.8}50%{opacity:.2}}`}</style>

        {/* ── PHOTO CAROUSEL ─────────────────────────────────────────── */}
        <div style={{ position: 'relative', marginBottom: '.4rem' }}>
          <AnnotatedPhoto
            src={photos[activePhotoIdx]}
            conditions={analysis.conditions}
            visibleAreas={visAreas || null}
            landmarkMap={landmarkMaps[activePhotoIdx] || null}
            photoIdx={activePhotoIdx}
            onTap={() => setExpanded(true)}
          />
          {/* Prev arrow */}
          {activePhotoIdx > 0 && (
            <button onClick={() => setActivePhotoIdx(p => p - 1)}
              style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,.45)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: 'white', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ‹
            </button>
          )}
          {/* Next arrow */}
          {activePhotoIdx < photos.length - 1 && (
            <button onClick={() => setActivePhotoIdx(p => p + 1)}
              style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'rgba(0,0,0,.45)', border: 'none', borderRadius: '50%', width: 36, height: 36, color: 'white', fontSize: '1.2rem', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              ›
            </button>
          )}
        </div>

        {/* Angle label + dot indicators */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '.6rem', marginBottom: '.75rem' }}>
          {photos.map((_, i) => (
            <div key={i} onClick={() => setActivePhotoIdx(i)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '.3rem' }}>
              <div style={{ width: i === activePhotoIdx ? 18 : 7, height: 7, borderRadius: 4, background: i === activePhotoIdx ? C.g600 : C.border, transition: 'all .2s' }} />
              {i === activePhotoIdx && <span style={{ fontSize: '.62rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: C.g700 }}>{ANGLE_LABELS[i]}</span>}
            </div>
          ))}
        </div>

        {/* Conditions for THIS photo */}
        {photoConds.length > 0 && (
          <div style={{ background: 'white', borderRadius: 14, border: `1px solid ${C.border}`, marginBottom: '1rem', overflow: 'hidden' }}>
            <div style={{ padding: '.6rem 1rem', borderBottom: `1px solid ${C.border}`, fontSize: '.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.09em', color: C.muted }}>
              Detected on {ANGLE_LABELS[activePhotoIdx]}
            </div>
            {photoConds.map((c, i) => {
              const origIdx = analysis.conditions.indexOf(c)
              return (
                <div key={i} style={{ display: 'flex', gap: '.7rem', alignItems: 'center', padding: '.6rem 1rem', borderBottom: i < photoConds.length - 1 ? `1px solid ${C.border}` : 'none', background: SEV_BG[c.severity] || 'white' }}>
                  <div style={{ width: 22, height: 22, borderRadius: '50%', background: condColor(origIdx), flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: '.65rem', fontWeight: 800 }}>{origIdx + 1}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontWeight: 600, fontSize: '.85rem', color: C.g900 }}>{c.name}</span>
                    {c.area && <span style={{ fontSize: '.72rem', color: C.muted, marginLeft: '.35rem' }}>· {c.area.replace(/_/g, ' ')}</span>}
                  </div>
                  <span style={{ fontSize: '.67rem', fontWeight: 700, color: SEV_COLOR[c.severity] || C.muted, textTransform: 'uppercase', flexShrink: 0 }}>{c.severity}</span>
                </div>
              )
            })}
          </div>
        )}

        {/* Fullscreen zoom modal */}
        {expanded && (
          <div onClick={() => setExpanded(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.93)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div style={{ maxWidth: 500, width: '100%' }} onClick={e => e.stopPropagation()}>
              <AnnotatedPhoto src={photos[activePhotoIdx]} conditions={analysis.conditions} visibleAreas={visAreas || null} landmarkMap={landmarkMaps[activePhotoIdx] || null} photoIdx={activePhotoIdx} onTap={() => setExpanded(false)} />
              <div style={{ textAlign: 'center', color: 'rgba(255,255,255,.4)', fontSize: '.65rem', marginTop: '.5rem' }}>Tap photo to close</div>
            </div>
          </div>
        )}

        {/* AI SKIN ANALYSIS summary */}
        <div style={{ background: C.headerGrad, borderRadius: 14, padding: '.9rem 1.1rem', color: 'white', marginBottom: '.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase', opacity: .7, marginBottom: '.15rem' }}>AI Skin Analysis</div>
            <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.1rem', lineHeight: 1.2 }}>{analysis.topConcern || 'Analysis Complete'}</div>
          </div>
          <div style={{ textAlign: 'right', fontSize: '.75rem', opacity: .85 }}>
            <div>Skin type</div>
            <div style={{ fontWeight: 700, textTransform: 'capitalize' }}>{analysis.skinType}</div>
          </div>
        </div>

        <div style={{ background: C.g50, borderRadius: 12, padding: '.9rem 1.1rem', marginBottom: '.75rem', fontSize: '.85rem', color: C.g800, lineHeight: 1.6 }}>
          {analysis.summary}
        </div>

        {/* Skincare tips */}
        {analysis.tips?.length > 0 && (
          <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 12, padding: '.8rem 1rem', marginBottom: '.75rem' }}>
            <div style={{ fontSize: '.6rem', letterSpacing: '.08em', textTransform: 'uppercase', color: '#92400E', fontWeight: 700, marginBottom: '.5rem' }}>What to do</div>
            {analysis.tips.map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-start', marginBottom: i < analysis.tips.length - 1 ? '.4rem' : 0 }}>
                <span style={{ color: '#D97706', fontSize: '.75rem', marginTop: 1, flexShrink: 0 }}>→</span>
                <span style={{ fontSize: '.78rem', color: '#78350F', lineHeight: 1.5 }}>{tip}</span>
              </div>
            ))}
          </div>
        )}

        {/* Consult urgency */}
        <div style={{ background: SEV_BG[analysis.consultUrgency === 'urgent' ? 'severe' : analysis.consultUrgency === 'soon' ? 'moderate' : 'mild'],
          border: `1px solid ${SEV_COLOR[analysis.consultUrgency === 'urgent' ? 'severe' : analysis.consultUrgency === 'soon' ? 'moderate' : 'mild']}30`,
          borderRadius: 12, padding: '.8rem 1.1rem', marginBottom: '.75rem', display: 'flex', gap: '.6rem', alignItems: 'center' }}>
          <span style={{ fontSize: '1.3rem' }}>{analysis.consultUrgency === 'urgent' ? '🚨' : analysis.consultUrgency === 'soon' ? '⚠️' : '✅'}</span>
          <div style={{ fontWeight: 600, fontSize: '.85rem', color: urgencyColor[analysis.consultUrgency] || C.g700 }}>
            {urgencyLabel[analysis.consultUrgency]}
          </div>
        </div>

        {saved && (
          <div style={{ background: '#F0FDF4', border: '1px solid #86EFAC', borderRadius: 10, padding: '.6rem 1rem', marginBottom: '.6rem', fontSize: '.78rem', color: '#166534', display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            ✅ Scan saved — your doctor can review it before your consultation.
          </div>
        )}

        <div style={{ background: '#fffbeb', border: '1px solid #f0e0a0', borderRadius: 10, padding: '.6rem 1rem', marginBottom: '1rem', fontSize: '.73rem', color: '#7a6020' }}>
          <strong>Note:</strong> Preliminary AI scan, not a medical diagnosis. A dermatologist will review before your consultation.
        </div>

        <div style={{ display: 'flex', gap: '.75rem', flexWrap: 'wrap' }}>
          <button onClick={() => navigate('/book-appointment')}
            style={{ flex: 2, minWidth: 160, padding: '.85rem', borderRadius: 12, background: C.g700, color: 'white', border: 'none', cursor: 'pointer', fontSize: '.9rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
            📅 Book Consultation — ₹500
          </button>
          <button onClick={retake}
            style={{ flex: 1, minWidth: 100, padding: '.85rem', borderRadius: 12, background: C.g50, color: C.g700, border: `1px solid ${C.border}`, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
            Scan Again
          </button>
        </div>
      </div>
    )
  }

  return null
}

// ── Main Page ──────────────────────────────────────────────────────────────────
export default function SkinQuizPage() {
  const { products } = useProducts()
  const navigate = useNavigate()
  // If returning from login with pending photos, go straight to scan mode
  const hasPending = !!sessionStorage.getItem('skin_quiz_pending_photos')
  const [mode, setMode]       = useState(hasPending ? 'scan' : 'choose')
  const [step, setStep]       = useState(0)
  const [answers, setAnswers] = useState({})
  const [results, setResults] = useState(null)
  const [analyzing, setAnalyzing] = useState(false)
  const [analyzeDots, setAnalyzeDots] = useState('')

  const currentStep = STEPS[step]
  const progress    = Math.round((step / STEPS.length) * 100)

  useEffect(() => {
    if (!analyzing) return
    const t = setInterval(() => setAnalyzeDots(d => d.length >= 3 ? '' : d + '.'), 400)
    return () => clearInterval(t)
  }, [analyzing])

  function choose(value) {
    const newAnswers = { ...answers, [currentStep.id]: value }
    setAnswers(newAnswers)
    if (step < STEPS.length - 1) {
      setStep(step + 1)
    } else {
      // Show a short "analyzing your skin profile" animation before results
      setAnalyzing(true)
      setTimeout(() => {
        setResults(getRecommendations(newAnswers.concern, products))
        setAnalyzing(false)
      }, 2400)
    }
  }

  function goBack() {
    if (step > 0) setStep(step - 1)
    else setMode('choose')
  }

  function restart() {
    setStep(0); setAnswers({}); setResults(null); setAnalyzing(false); setMode('choose')
  }

  // ── Mode chooser ────────────────────────────────────────────────────────────
  if (mode === 'choose') return (
    <div style={{ background: '#f9fdf9', minHeight: '80vh' }}>
      <div style={{ background: C.headerGrad, padding: '1.9rem 2rem 1.6rem', textAlign: 'center', color: 'white' }}>
        <div style={{ fontSize: '1.8rem', marginBottom: '.35rem' }}>🌿</div>
        <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 'clamp(1.4rem,3vw,1.9rem)', fontWeight: 400, marginBottom: '.4rem' }}>
          Understand Your Skin
        </h1>
        <p style={{ opacity: .8, fontSize: '.9rem' }}>Choose how you'd like to get your personalised recommendation</p>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '2.5rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {/* AI Scan card */}
        <button onClick={() => setMode('scan')}
          style={{ background: 'white', border: `2px solid ${C.g400}`, borderRadius: 20, padding: '1.75rem', cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans',sans-serif", position: 'relative', overflow: 'hidden' }}
          onMouseEnter={e => e.currentTarget.style.borderColor = C.g600}
          onMouseLeave={e => e.currentTarget.style.borderColor = C.g400}>
          <div style={{ position: 'absolute', top: 12, right: 14, background: C.g600, color: 'white', fontSize: '.6rem', fontWeight: 700, padding: '3px 10px', borderRadius: 999, letterSpacing: '.08em' }}>NEW</div>
          <div style={{ fontSize: '2.2rem', marginBottom: '.75rem' }}>🔬</div>
          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: C.g900, marginBottom: '.35rem' }}>AI Skin Scan</div>
          <div style={{ fontSize: '.83rem', color: C.muted, lineHeight: 1.5, marginBottom: '.75rem' }}>
            Take a selfie — AI instantly detects your skin type, acne, pigmentation, and more. Like Cureskin but for your clinic.
          </div>
          <div style={{ display: 'flex', gap: '1.25rem' }}>
            {[['📷','Camera scan'],['🤖','AI analysis'],['👨‍⚕️','Doctor review']].map(([icon,label]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '.3rem', fontSize: '.72rem', color: C.g600, fontWeight: 500 }}>
                <span>{icon}</span><span>{label}</span>
              </div>
            ))}
          </div>
        </button>

        <div style={{ textAlign: 'center', color: C.muted, fontSize: '.8rem', fontStyle: 'italic' }}>— or —</div>

        {/* Quiz card */}
        <button onClick={() => setMode('quiz')}
          style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 20, padding: '1.75rem', cursor: 'pointer', textAlign: 'left', fontFamily: "'DM Sans',sans-serif" }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = C.g400; e.currentTarget.style.background = C.g50 }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'white' }}>
          <div style={{ fontSize: '2.2rem', marginBottom: '.75rem' }}>📝</div>
          <div style={{ fontWeight: 700, fontSize: '1.1rem', color: C.g900, marginBottom: '.35rem' }}>Skin Analysis Quiz</div>
          <div style={{ fontSize: '.83rem', color: C.muted, lineHeight: 1.5 }}>
            Answer a few quick questions about your concern, skin type, and goals. Get a personalised routine and matched products in about a minute.
          </div>
        </button>
      </div>
    </div>
  )

  // ── AI Scan mode ─────────────────────────────────────────────────────────────
  if (mode === 'scan') return (
    <div style={{ background: '#f9fdf9', minHeight: '80vh' }}>
      <div style={{ background: 'white', borderBottom: `1px solid ${C.border}`, padding: '.9rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={() => setMode('choose')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif", padding: 0 }}>
          ← Back
        </button>
        <span style={{ fontWeight: 600, color: C.g900, fontSize: '.9rem' }}>AI Skin Scan</span>
      </div>
      <SkinScan onBack={() => setMode('choose')} />
    </div>
  )

  // ── Analyzing animation ───────────────────────────────────────────────────────
  if (analyzing) return (
    <div style={{ background: `linear-gradient(135deg, ${C.g900} 0%, ${C.g700} 100%)`, minHeight: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', textAlign: 'center', padding: '2rem' }}>
      <div>
        <div style={{ width: 84, height: 84, margin: '0 auto 1.5rem', borderRadius: '50%', border: '3px solid rgba(255,255,255,.2)', borderTopColor: 'white', animation: 'spin 1s linear infinite' }} />
        <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.7rem', marginBottom: '.6rem' }}>Analyzing your skin profile{analyzeDots}</div>
        <div style={{ opacity: .75, fontSize: '.9rem', maxWidth: 380, margin: '0 auto', lineHeight: 1.6 }}>
          Matching your concern, skin type and goals to the right dermatologist-backed products…
        </div>
      </div>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  // ── Quiz mode ─────────────────────────────────────────────────────────────────
  if (results) {
    const labelFor = (id) => { const s = STEPS.find(x => x.id === id); return s?.options.find(o => o.value === answers[id])?.label || '' }
    const concernLabel = labelFor('concern')
    const profileChips = [
      concernLabel && { k: 'Concern', v: concernLabel },
      labelFor('type') && { k: 'Skin type', v: labelFor('type') },
      labelFor('goal') && { k: 'Goal', v: labelFor('goal') },
      labelFor('age') && { k: 'Age', v: labelFor('age') },
    ].filter(Boolean)
    const routine = answers.concern === 'hair'
      ? ['Gentle shampoo', 'Scalp / hair growth serum', 'Leave-in / conditioner']
      : ['Gentle cleanser', `Targeted treatment for ${concernLabel.toLowerCase() || 'your concern'}`, 'Moisturiser', 'Sunscreen (every morning)']
    return (
      <div style={{ background: '#f9fdf9', paddingBottom: '4rem' }}>
        <div style={{ background: C.headerGrad, padding: '1.9rem 2rem 1.6rem', textAlign: 'center', color: 'white' }}>
          <div style={{ fontSize: '1.8rem', marginBottom: '.35rem' }}>🌿</div>
          <h1 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 'clamp(1.4rem,3vw,1.9rem)', fontWeight: 400, marginBottom: '.4rem' }}>Your Personalised Plan</h1>
          <p style={{ opacity: .8, fontSize: '.9rem' }}>Based on your concern: <strong>{concernLabel}</strong></p>
        </div>
        <div style={{ maxWidth: 900, margin: '0 auto', padding: '2.5rem 1.5rem' }}>

          {/* Skin profile summary */}
          <div style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 16, padding: '1.25rem 1.5rem', marginBottom: '1.5rem' }}>
            <div style={{ fontSize: '.6rem', letterSpacing: '.12em', textTransform: 'uppercase', color: C.g500, marginBottom: '.75rem', fontWeight: 700 }}>Your Skin Profile</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem', marginBottom: '1rem' }}>
              {profileChips.map(c => (
                <span key={c.k} style={{ background: C.g50, border: `1px solid ${C.border}`, borderRadius: 999, padding: '.3rem .8rem', fontSize: '.75rem', color: C.g800 }}>
                  <strong style={{ color: C.g600 }}>{c.k}:</strong> {c.v}
                </span>
              ))}
            </div>
            <div style={{ fontSize: '.6rem', letterSpacing: '.12em', textTransform: 'uppercase', color: C.g500, marginBottom: '.5rem', fontWeight: 700 }}>Suggested Routine</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem', alignItems: 'center' }}>
              {routine.map((r, i) => (
                <React.Fragment key={i}>
                  <span style={{ fontSize: '.8rem', color: C.g800 }}>{i + 1}. {r}</span>
                  {i < routine.length - 1 && <span style={{ color: C.g400 }}>→</span>}
                </React.Fragment>
              ))}
            </div>
          </div>

          {results.length === 0 ? (
            <p style={{ textAlign: 'center', color: C.muted, padding: '3rem' }}>No exact matches — our doctor will personalise your plan.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
              {results.map(p => (
                <div key={p.id} onClick={() => navigate('/product/' + p.id)}
                  style={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: 16, overflow: 'hidden', cursor: 'pointer', boxShadow: '0 2px 12px rgba(35,82,51,.07)' }}>
                  <div style={{ height: 160, background: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {p.imgs?.[0]
                      ? <img src={p.imgs[0]} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                      : <span style={{ fontSize: '3rem', opacity: .3 }}>🧴</span>}
                  </div>
                  <div style={{ padding: '1rem' }}>
                    <div style={{ fontSize: '.6rem', letterSpacing: '.1em', textTransform: 'uppercase', color: C.g500, marginBottom: '.3rem' }}>{p.cat}</div>
                    <div style={{ fontSize: '.82rem', fontWeight: 600, color: C.g900, marginBottom: '.5rem', lineHeight: 1.35 }}>{p.name}</div>
                    <div style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.15rem', color: C.g700 }}>&#8377;{p.price}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ background: '#fffbeb', border: '1px solid #f0e0a0', borderRadius: 12, padding: '1rem 1.25rem', marginBottom: '2rem', fontSize: '.8rem', color: '#7a6020' }}>
            <strong>Note:</strong> These are general suggestions. For personalised prescriptions, consult our doctor.
          </div>
          <div style={{ background: `linear-gradient(135deg, ${C.g800}, ${C.g600})`, borderRadius: 20, padding: '2.5rem', textAlign: 'center', color: 'white' }}>
            <div style={{ fontSize: '1.75rem', marginBottom: '.75rem' }}>👨‍⚕️</div>
            <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: '1.5rem', fontWeight: 400, marginBottom: '.6rem' }}>Get Expert Guidance</h2>
            <p style={{ opacity: .85, fontSize: '.88rem', maxWidth: 420, margin: '0 auto 1.5rem' }}>Our dermatologists review your skin history and prescribe the right products for your condition.</p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button onClick={() => navigate('/book-appointment')}
                style={{ background: C.g400, color: C.g900, border: 'none', padding: '.75rem 1.75rem', borderRadius: 10, cursor: 'pointer', fontSize: '.85rem', fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>
                📅 Book a Consultation
              </button>
              <button onClick={restart}
                style={{ background: 'rgba(255,255,255,.1)', color: 'white', border: '1px solid rgba(255,255,255,.2)', padding: '.75rem 1.75rem', borderRadius: 10, cursor: 'pointer', fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif" }}>
                🔄 Retake Quiz
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={{ background: '#f9fdf9' }}>
      <div style={{ background: 'white', borderBottom: `1px solid ${C.border}`, padding: '1rem 2rem', display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={goBack}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, fontSize: '.85rem', fontFamily: "'DM Sans',sans-serif", padding: 0 }}>
          ← Back
        </button>
        <div style={{ flex: 1, background: C.g100, borderRadius: 999, height: 6, overflow: 'hidden' }}>
          <div style={{ background: C.g500, height: '100%', borderRadius: 999, width: progress + '%', transition: 'width .4s ease' }} />
        </div>
        <span style={{ fontSize: '.8rem', color: C.muted, whiteSpace: 'nowrap' }}>{step + 1} / {STEPS.length}</span>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '3rem 1.5rem', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>
          {['🔍', '🧬', '🎂', '🧴', '🌸', '🧼', '🎯', '🛡️', '💊'][step]}
        </div>
        <h2 style={{ fontFamily: "'DM Serif Display',serif", fontSize: 'clamp(1.4rem,4vw,2rem)', color: C.g900, marginBottom: '.6rem', textAlign: 'center', lineHeight: 1.2 }}>
          {currentStep.question}
        </h2>
        <p style={{ color: C.muted, fontSize: '.88rem', marginBottom: '2rem', textAlign: 'center' }}>{currentStep.subtitle}</p>

        <div style={{ display: 'grid', gridTemplateColumns: currentStep.options.length > 4 ? '1fr 1fr' : '1fr', gap: '.75rem', width: '100%' }}>
          {currentStep.options.map(opt => (
            <button key={opt.value} onClick={() => choose(opt.value)}
              style={{ background: 'white', border: `2px solid ${C.border}`, borderRadius: 14, padding: '1rem 1.25rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '1rem', textAlign: 'left', fontFamily: "'DM Sans',sans-serif", color: C.g900, fontSize: '.9rem', fontWeight: 500 }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = C.g500; e.currentTarget.style.background = C.g50 }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.background = 'white' }}>
              <span style={{ fontSize: '1.6rem', flexShrink: 0 }}>{opt.emoji}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '.5rem', marginTop: '2.5rem' }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{ width: i === step ? 20 : 8, height: 8, borderRadius: 999, background: i === step ? C.g600 : i < step ? C.g400 : C.border, transition: 'all .3s' }} />
          ))}
        </div>
      </div>
    </div>
  )
}
